import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogoSvg } from "../svgs/LogoSvg";
import { navigationMenus, PATH_CONTACT } from "../../utils/constants";
import { PrimaryButton } from "../common/PrimaryButton";
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

export const Header = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSectionsOpen, setMobileSectionsOpen] = useState<{ services: boolean; about: boolean; infos: boolean }>({ services: false, about: false, infos: false });
  const [openDropdown, setOpenDropdown] = useState<null | "services" | "about" | "infos">(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSectionsOpen({ services: false, about: false, infos: false });
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  // Periodic animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimate(true);
      setTimeout(() => setAnimate(false), 2000); // Animation duration
    }, 30000); // Repeat every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const isMenuActive = (items: { href: string }[]) =>
    items.some((item) => location.pathname === item.href);

  return (
    <>
      <div className="h-16 sm:h-20" />

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
          bg-white dark:bg-gray-900 border-b border-gray-200/60 dark:border-gray-700/40
          ${scrolled ? "shadow-sm" : ""}`}
      >
        <nav>
          <div className="flex h-16 sm:h-20 items-center justify-between px-5 sm:px-8 lg:px-12">
            {/* Logo - Left aligned */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center gap-4">
                <span className="sr-only">ERGOTHERAPIE UND ZEIT(T)RÄUME</span>
                <LogoSvg classNames="h-7 sm:h-9 xl:h-11 w-auto fill-current text-ergo-500 dark:text-ergo-400 transition-transform duration-300 hover:scale-105" />
                <div className="flex xl:hidden 2xl:flex flex-col">
                  <span className="text-xs sm:text-sm xl:text-base font-medium tracking-wider text-gray-900 dark:text-white">
                    ERGOTHERAPIE
                  </span>
                  <span className="text-xs sm:text-sm xl:text-base font-medium tracking-wider text-ergo-600 dark:text-ergo-400">
                    UND ZEIT(T)RÄUME
                  </span>
                </div>
              </Link>
            </div>

            {/* Navigation Links - Centered with flex-grow */}
            <div className="flex-grow hidden xl:flex justify-center">
              <nav className="flex items-center gap-6 2xl:gap-10">
                {/* Home link */}
                <Link
                  to={navigationMenus.home.href}
                  className={`relative px-2 py-2 text-sm xl:text-base tracking-wide transition-all duration-300 group ${
                    location.pathname === navigationMenus.home.href
                      ? "text-ergo-600 dark:text-ergo-400 font-medium"
                      : "text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 font-normal"
                  }`}
                >
                  {navigationMenus.home.name}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full transform origin-left transition-transform duration-300 ease-out ${
                      location.pathname === navigationMenus.home.href
                        ? "bg-ergo-400 scale-x-100"
                        : "bg-ergo-300 scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </Link>

                {/* Leistungen dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDropdown("services")}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className={`relative inline-flex items-center gap-1 px-2 py-2 text-sm xl:text-base transition-colors group ${
                    isMenuActive(navigationMenus.services.items)
                      ? "text-ergo-600 dark:text-ergo-400 font-medium"
                      : "text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 font-normal"
                  }`}>
                    {navigationMenus.services.label}
                    <ChevronDownIcon className="h-4 w-4" />
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full transform origin-left transition-transform duration-300 ease-out ${
                        isMenuActive(navigationMenus.services.items)
                          ? "bg-ergo-400 scale-x-100"
                          : "bg-ergo-300 scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </button>
                  <div
                    className={`${openDropdown === "services" ? "visible opacity-100" : "invisible opacity-0"} transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[520px]`}
                  >
                    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-900 shadow-lg p-3">
                      <div className="grid grid-cols-2 gap-1">
                        {navigationMenus.services.items.map((item, index) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              location.pathname === item.href
                                ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            }`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Über uns dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDropdown("about")}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className={`relative inline-flex items-center gap-1 px-2 py-2 text-sm xl:text-base transition-colors group ${
                    isMenuActive(navigationMenus.about.items)
                      ? "text-ergo-600 dark:text-ergo-400 font-medium"
                      : "text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 font-normal"
                  }`}>
                    {navigationMenus.about.label}
                    <ChevronDownIcon className="h-4 w-4" />
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full transform origin-left transition-transform duration-300 ease-out ${
                        isMenuActive(navigationMenus.about.items)
                          ? "bg-ergo-400 scale-x-100"
                          : "bg-ergo-300 scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </button>
                  <div
                    className={`${openDropdown === "about" ? "visible opacity-100" : "invisible opacity-0"} transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[280px]`}
                  >
                    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-900 shadow-lg p-3">
                      <div className="grid grid-cols-1 gap-1">
                        {navigationMenus.about.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              location.pathname === item.href
                                ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            }`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Infos dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDropdown("infos")}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className={`relative inline-flex items-center gap-1 px-2 py-2 text-sm xl:text-base transition-colors group ${
                    isMenuActive(navigationMenus.infos.items)
                      ? "text-ergo-600 dark:text-ergo-400 font-medium"
                      : "text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 font-normal"
                  }`}>
                    {navigationMenus.infos.label}
                    <ChevronDownIcon className="h-4 w-4" />
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full transform origin-left transition-transform duration-300 ease-out ${
                        isMenuActive(navigationMenus.infos.items)
                          ? "bg-ergo-400 scale-x-100"
                          : "bg-ergo-300 scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </button>
                  <div
                    className={`${openDropdown === "infos" ? "visible opacity-100" : "invisible opacity-0"} transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[320px]`}
                  >
                    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-900 shadow-lg p-3">
                      <div className="grid grid-cols-1 gap-1">
                        {navigationMenus.infos.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              location.pathname === item.href
                                ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            }`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>

            {/* Contact Button - Right aligned */}
            <div className="flex-shrink-0 hidden xl:block">
              <Link
                to={PATH_CONTACT}
                className="inline-flex items-center gap-2 rounded-full border border-ergo-500/30 dark:border-ergo-400/30
                  bg-ergo-50/60 dark:bg-ergo-900/30 px-5 py-2
                  text-sm font-medium text-ergo-700 dark:text-ergo-300
                  hover:bg-ergo-100 dark:hover:bg-ergo-900/50 hover:border-ergo-500/50 dark:hover:border-ergo-400/50
                  transition-all duration-200"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                Kontakt
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="flex xl:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-ergo-600 dark:text-ergo-400"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Menü öffnen</span>
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu panel */}
          <div
            className={`fixed inset-0 z-50 xl:hidden bg-white dark:bg-gray-900 transition-all duration-300 ease-in-out
              ${mobileMenuOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-full"}`}
            style={{ top: "64px" }}
          >
            <div className="flex flex-col h-full">
              {/* Navigation Links - Mobile grouped sections */}
              <div className="flex-grow overflow-y-auto px-6 pt-8 pb-8">
                <div className="space-y-6">
                  {/* Home */}
                  <Link
                    to={navigationMenus.home.href}
                    className={`block rounded-xl px-4 py-4 text-center transition-colors ${
                      location.pathname === navigationMenus.home.href
                        ? "bg-ergo-50/50 dark:bg-gray-800/50 font-semibold text-ergo-600 dark:text-ergo-400"
                        : "bg-gray-50 dark:bg-gray-800/30 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                    }`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setMobileSectionsOpen({ services: false, about: false, infos: false });
                    }}
                  >
                    {navigationMenus.home.name}
                  </Link>

                  {/* Leistungen */}
                  <div>
                    <button
                      onClick={() => setMobileSectionsOpen((s) => ({ ...s, services: !s.services }))}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                        isMenuActive(navigationMenus.services.items)
                          ? "bg-ergo-50/50 dark:bg-gray-800/50 border-ergo-200 dark:border-ergo-700/40"
                          : "bg-white dark:bg-gray-900 border-gray-200/60 dark:border-gray-700/40"
                      }`}
                    >
                      <span className={`text-base font-medium ${
                        isMenuActive(navigationMenus.services.items)
                          ? "text-ergo-600 dark:text-ergo-400"
                          : "text-gray-800 dark:text-gray-200"
                      }`}>{navigationMenus.services.label}</span>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${mobileSectionsOpen.services ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${mobileSectionsOpen.services ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {navigationMenus.services.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-3 rounded-lg transition-colors ${
                                location.pathname === item.href
                                  ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                  : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                              }`}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileSectionsOpen({ services: false, about: false, infos: false });
                            }}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Über uns */}
                  <div>
                    <button
                      onClick={() => setMobileSectionsOpen((s) => ({ ...s, about: !s.about }))}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                        isMenuActive(navigationMenus.about.items)
                          ? "bg-ergo-50/50 dark:bg-gray-800/50 border-ergo-200 dark:border-ergo-700/40"
                          : "bg-white dark:bg-gray-900 border-gray-200/60 dark:border-gray-700/40"
                      }`}
                    >
                      <span className={`text-base font-medium ${
                        isMenuActive(navigationMenus.about.items)
                          ? "text-ergo-600 dark:text-ergo-400"
                          : "text-gray-800 dark:text-gray-200"
                      }`}>{navigationMenus.about.label}</span>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${mobileSectionsOpen.about ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${mobileSectionsOpen.about ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 gap-2">
                          {navigationMenus.about.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-3 rounded-lg transition-colors ${
                                location.pathname === item.href
                                  ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                  : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                              }`}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileSectionsOpen({ services: false, about: false, infos: false });
                            }}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Infos */}
                  <div>
                    <button
                      onClick={() => setMobileSectionsOpen((s) => ({ ...s, infos: !s.infos }))}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                        isMenuActive(navigationMenus.infos.items)
                          ? "bg-ergo-50/50 dark:bg-gray-800/50 border-ergo-200 dark:border-ergo-700/40"
                          : "bg-white dark:bg-gray-900 border-gray-200/60 dark:border-gray-700/40"
                      }`}
                    >
                      <span className={`text-base font-medium ${
                        isMenuActive(navigationMenus.infos.items)
                          ? "text-ergo-600 dark:text-ergo-400"
                          : "text-gray-800 dark:text-gray-200"
                      }`}>{navigationMenus.infos.label}</span>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${mobileSectionsOpen.infos ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${mobileSectionsOpen.infos ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 gap-2">
                          {navigationMenus.infos.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-3 rounded-lg transition-colors ${
                                location.pathname === item.href
                                  ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                  : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                              }`}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileSectionsOpen({ services: false, about: false, infos: false });
                            }}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Button */}
              <div className="p-6">
                <PrimaryButton
                  to={PATH_CONTACT}
                  periodic
                  className="w-full py-3 text-base"
                >
                  Kontakt
                </PrimaryButton>
              </div>
            </div>
          </div>

          {/* Background overlay when mobile menu is open */}
          <div
            className={`fixed inset-0 bg-black/10 dark:bg-black/20 backdrop-blur-sm transition-opacity duration-300 xl:hidden
              ${mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            style={{ top: "64px" }}
            onClick={() => setMobileMenuOpen(false)}
          />
        </nav>
      </header>
    </>
  );
};
